import CurrencyFormatter from "currency-formatter"
import GeneralHelper from "../../utils/GeneralHelper";
import FinancialAccount from "../FinancialAccount";

export  class BalanceSheetReportCategory {
  constructor({
    manuality = "AUTO",
    categoryName,
    categoryValue,
    balance = 0,
    analystNote = "",
    liquidationPercentage = 0,
    analystTool = 100,
    manualAdjustedValue = 0,
    excessOrNonOperating = 0,
    categoryGroupName
  }) {
    this.manuality = manuality
    this.categoryName = categoryName
    this.categoryValue = categoryValue
    this.balance = balance
    this.analystNote = analystNote
    this.liquidationPercentage = liquidationPercentage
    this.analystTool = analystTool
    this.manualAdjustedValue = manualAdjustedValue
    this.excessOrNonOperating = excessOrNonOperating
    this.categoryGroupName = categoryGroupName
  }

  get formattedBalance() {
    return CurrencyFormatter.format(this.balance, { code: "USD", symbol: "$ " })
  }

  get adjustments() {
    return (this.balance - (this.balance * this.analystToolMultiplierFactor)) * -1
  }

  get adjustedValue() {
    if(this.manuality === "AUTO" || this.manualAdjustedValue === 0) return this.balance + this.adjustments;
    else return this.manualAdjustedValue;
  }

  get liquidationValue() {
    return this.adjustedValue * this.liquidationPercentageMultiplierFactor
  }

  get adjustmentsFormatted() {
    return CurrencyFormatter.format(this.adjustments, { code: "USD", symbol: "$ " })
  }

  get adjustedValueFormatted() {
    return CurrencyFormatter.format(this.adjustedValue, { code: "USD", symbol: "$ " })
  }

  get liquidationValueFormatted() {
    return CurrencyFormatter.format(this.liquidationValue, { code: "USD", symbol: "$ " })
  }

  get analystToolMultiplierFactor() {
    return CurrencyFormatter.unformat(this.analystTool, { code: "USD" }) / 100
  }

  get liquidationPercentageMultiplierFactor() {
    return CurrencyFormatter.unformat(this.liquidationPercentage, { code: "USD" }) / 100
  }

  get excessOrNonOperatingUnformatted() {
    return CurrencyFormatter.unformat(this.excessOrNonOperating, { code: "USD" })
  }
}

export class BalanceSheetReportCategoryGroup {
  constructor({
    groupName,
    categories
  }) {
    this.groupName = groupName
    this.categories = categories
  }

  get totalBalance() {
    return GeneralHelper.sumObjectAttributeFromArray(this.categories, "balance", {
      applyCurrencyFormat: true
    })
  }

  get totalAdjustments() {
    return GeneralHelper.sumObjectAttributeFromArray(this.categories, "adjustments", {
      applyCurrencyFormat: true
    })
  }

  get totalAdjustedValue() {
    return GeneralHelper.sumObjectAttributeFromArray(this.categories, "adjustedValue", {
      applyCurrencyFormat: true
    })
  }

  get totalLiquidationValue() {
    return GeneralHelper.sumObjectAttributeFromArray(this.categories, "liquidationValue", {
      applyCurrencyFormat: true
    })
  }

  get totalLiquidation() {
    return Math.round(parseFloat(this.totalLiquidationValue.replace(/[, $]/g,""))
      / parseFloat(this.totalAdjustedValue.replace(/[, $]/g,"")) * 10000) / 100 + " %";
  }

  get totalExcessOrNonOperating() {
    return GeneralHelper.sumObjectAttributeFromArray(this.categories, "excessOrNonOperatingUnformatted", {
      applyCurrencyFormat: true
    })
  }
}

export class BalanceSheetReport {
  static calculateSummary(balanceSheetReport) {
    const { currentAssets, longTermAssets, currentLiabilities, longTermLiabilities } =  FinancialAccount.getAllowedBalanceSheetItemGroups()

    const assets = []
    balanceSheetReport.filter((report) => [currentAssets, longTermAssets].includes(report.groupName))
      .forEach((group) => {
        group.categories.forEach((category) => assets.push(category))
      })

    const liabilities = []
    balanceSheetReport.filter((report) => [currentLiabilities, longTermLiabilities].includes(report.groupName))
      .forEach((group) => {
        group.categories.forEach((category) => liabilities.push(category))
      })

    const assetsSummary = {
      totalBalance: GeneralHelper.sumObjectAttributeFromArray(assets, "balance"),
      totalAdjustments: GeneralHelper.sumObjectAttributeFromArray(assets, "adjustments"),
      totalAdjustedValue: GeneralHelper.sumObjectAttributeFromArray(assets, "adjustedValue"),
      totalLiquidationValue: GeneralHelper.sumObjectAttributeFromArray(assets, "liquidationValue"),
      totalLiquidation: 0,
      totalExcessOrNonOperating: GeneralHelper.sumObjectAttributeFromArray(assets, "excessOrNonOperatingUnformatted")
    }

    assetsSummary.totalLiquidation = assetsSummary.totalLiquidationValue / assetsSummary.totalAdjustedValue * 100

    const liabilitiesSummary = {
      totalBalance: GeneralHelper.sumObjectAttributeFromArray(liabilities, "balance"),
      totalAdjustments: GeneralHelper.sumObjectAttributeFromArray(liabilities, "adjustments"),
      totalAdjustedValue: GeneralHelper.sumObjectAttributeFromArray(liabilities, "adjustedValue"),
      totalLiquidationValue: GeneralHelper.sumObjectAttributeFromArray(liabilities, "liquidationValue"),
      totalLiquidation: 0,
      totalExcessOrNonOperating: GeneralHelper.sumObjectAttributeFromArray(liabilities, "excessOrNonOperatingUnformatted")
    }

    liabilitiesSummary.totalLiquidation = liabilitiesSummary.totalLiquidationValue / liabilitiesSummary.totalAdjustedValue * 100

    const equitySummary = {
      totalBalance: assetsSummary.totalBalance - liabilitiesSummary.totalBalance,
      totalAdjustments: 0,
      totalAdjustedValue: assetsSummary.totalAdjustedValue - liabilitiesSummary.totalAdjustedValue,
      totalLiquidationValue: 0,
      totalLiquidation: 0,
      totalExcessOrNonOperating: 0
    }

    equitySummary.totalAdjustments = equitySummary.totalAdjustedValue - equitySummary.totalBalance
    equitySummary.totalLiquidationValue = assetsSummary.totalLiquidationValue - liabilitiesSummary.totalLiquidationValue
    equitySummary.totalExcessOrNonOperating = assetsSummary.totalExcessOrNonOperating - liabilitiesSummary.totalExcessOrNonOperating
    equitySummary.totalLiquidation = equitySummary.totalLiquidationValue / equitySummary.totalAdjustedValue * 100

    const summary = {
      assets: {
        totalBalance: assetsSummary.totalBalance,
        totalBalanceFormatted: CurrencyFormatter.format(assetsSummary.totalBalance, { code: "USD", symbol: "$ " }),
        totalAdjustments: assetsSummary.totalAdjustments,
        totalAdjustmentsFormatted: CurrencyFormatter.format(assetsSummary.totalAdjustments, { code: "USD", symbol: "$ " }),
        totalAdjustedValue: assetsSummary.totalAdjustedValue,
        totalAdjustedValueFormatted: CurrencyFormatter.format(assetsSummary.totalAdjustedValue, { code: "USD", symbol: "$ " }),
        totalLiquidationValue: assetsSummary.totalLiquidationValue,
        totalLiquidationValueFormatted: CurrencyFormatter.format(assetsSummary.totalLiquidationValue, { code: "USD", symbol: "$ " }),
        totalLiquidation: assetsSummary.totalLiquidation,
        totalLiquidationFormatted: CurrencyFormatter.format(assetsSummary.totalLiquidation, { code: "" }) + " %",
        totalExcessOrNonOperating: assetsSummary.totalExcessOrNonOperating,
        totalExcessOrNonOperatingFormatted: CurrencyFormatter.format(assetsSummary.totalExcessOrNonOperating, { code: "USD", symbol: "$ " })
      },
      liabilities: {
        totalBalance: liabilitiesSummary.totalBalance,
        totalBalanceFormatted: CurrencyFormatter.format(liabilitiesSummary.totalBalance, { code: "USD", symbol: "$ " }),
        totalAdjustments: liabilitiesSummary.totalAdjustments,
        totalAdjustmentsFormatted: CurrencyFormatter.format(liabilitiesSummary.totalAdjustments, { code: "USD", symbol: "$ " }),
        totalAdjustedValue: liabilitiesSummary.totalAdjustedValue,
        totalAdjustedValueFormatted: CurrencyFormatter.format(liabilitiesSummary.totalAdjustedValue, { code: "USD", symbol: "$ " }),
        totalLiquidationValue: liabilitiesSummary.totalLiquidationValue,
        totalLiquidationValueFormatted: CurrencyFormatter.format(liabilitiesSummary.totalLiquidationValue, { code: "USD", symbol: "$ " }),
        totalLiquidation: liabilitiesSummary.totalLiquidation,
        totalLiquidationFormatted: CurrencyFormatter.format(liabilitiesSummary.totalLiquidation, { code: "" }) + " %",
        totalExcessOrNonOperating: liabilitiesSummary.totalExcessOrNonOperating,
        totalExcessOrNonOperatingFormatted: CurrencyFormatter.format(liabilitiesSummary.totalExcessOrNonOperating, { code: "USD", symbol: "$ " })
      },
      equity: {
        totalBalance: equitySummary.totalBalance,
        totalBalanceFormatted: CurrencyFormatter.format(
          equitySummary.totalBalance,
          { code: "USD", symbol: "$ "}
        ),
        totalAdjustments: equitySummary.totalAdjustments,
        totalAdjustmentsFormatted: CurrencyFormatter.format(
          equitySummary.totalAdjustments,
          { code: "USD", symbol: "$ " }
        ),
        totalAdjustedValue: equitySummary.totalAdjustedValue,
        totalAdjustedValueFormatted: CurrencyFormatter.format(
          equitySummary.totalAdjustedValue,
          { code: "USD", symbol: "$ " }
        ),
        totalLiquidationValue: equitySummary.totalLiquidationValue,
        totalLiquidationValueFormatted: CurrencyFormatter.format(
          equitySummary.totalLiquidationValue,
          { code: "USD", symbol: "$ " }
        ),
        totalLiquidation: equitySummary.totalLiquidation,
        totalLiquidationFormatted: CurrencyFormatter.format(
          equitySummary.totalLiquidation,
          { code: "" }
        ) + " %",
        totalExcessOrNonOperating: equitySummary.totalExcessOrNonOperating,
        totalExcessOrNonOperatingFormatted: CurrencyFormatter.format(
          equitySummary.totalExcessOrNonOperating,
          { code: "USD", symbol: "$ " }
        )
      }
    }


    return summary
  }
}
